import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PhoneIcon, MoonIcon } from '@heroicons/react/24/outline';
import { auth } from '../firebase';
import { RecaptchaVerifier, signInWithPhoneNumber, PhoneAuthProvider, signInWithCredential } from 'firebase/auth';
import { createUserProfile } from '../firebase/collections';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

function Navbar() {
  const [isVerified, setIsVerified] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState('phone');
  const [isLoading, setIsLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [cooldownTimer, setCooldownTimer] = useState(0);
  const [error, setError] = useState('');
  const [verificationId, setVerificationId] = useState('');

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setIsVerified(!!user);
      setCurrentUser(user);
    });

    return () => unsubscribe();
  }, []); // Remove isVerified dependency to prevent re-runs

  useEffect(() => {
    // Cleanup any existing reCAPTCHA widgets when component unmounts
    return () => {
      const container = document.querySelector('#recaptcha-container');
      if (container) {
        container.innerHTML = '';
      }
    };
  }, []);

  const setupRecaptcha = async () => {
    console.log('Setting up reCAPTCHA...');
    try {
      // Clear any existing verifiers and containers
      if (window.recaptchaVerifier) {
        try {
          await window.recaptchaVerifier.clear();
          window.recaptchaVerifier = null;
        } catch (e) {
          console.log('Error clearing existing verifier:', e);
        }
      }

      // Remove any existing containers
      const existingContainer = document.getElementById('recaptcha-container');
      if (existingContainer) {
        existingContainer.remove();
      }

      // Create a new container
      const container = document.createElement('div');
      container.id = 'recaptcha-container';
      document.body.appendChild(container);

      // Create new verifier with invisible reCAPTCHA
      const verifier = new RecaptchaVerifier(auth, container, {
        size: 'invisible',
        callback: (response) => {
          console.log('reCAPTCHA solved successfully');
        },
        'expired-callback': () => {
          console.log('reCAPTCHA expired');
          if (window.recaptchaVerifier) {
            window.recaptchaVerifier.clear();
            window.recaptchaVerifier = null;
          }
        }
      });

      // Store the verifier instance
      window.recaptchaVerifier = verifier;

      console.log('Rendering reCAPTCHA...');
      await verifier.render();
      console.log('reCAPTCHA rendered successfully');
      return verifier;
    } catch (error) {
      console.error('reCAPTCHA setup failed:', error);
      throw error;
    }
  };

  const formatPhoneNumber = (value) => {
    if (!value) return value;
    
    // Remove all non-numeric characters
    const phoneNumber = value.replace(/[^\d+]/g, '');
    
    // Ensure it starts with +1
    if (!phoneNumber.startsWith('+')) {
      if (phoneNumber.startsWith('1')) {
        return '+' + phoneNumber;
      }
      return '+1' + phoneNumber;
    }
    
    return phoneNumber;
  };

  const handlePhoneChange = (e) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    setPhoneNumber(formattedPhone);
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    console.log('Sending verification code...');

    try {
      const formattedPhone = phoneNumber;
      console.log('Formatted phone number:', formattedPhone);
      
      // Validate phone number format
      if (!formattedPhone.match(/^\+1\d{10}$/)) {
        alert('Please enter a valid US/Canada phone number (+1 followed by 10 digits)');
        return;
      }

      // Setup reCAPTCHA first
      const recaptchaVerifier = await setupRecaptcha();
      
      // Add a small delay to ensure reCAPTCHA is fully initialized
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('Starting phone number verification...');
      const confirmation = await signInWithPhoneNumber(auth, formattedPhone, recaptchaVerifier);
      console.log('Verification code sent successfully!');
      
      // Store the verification ID
      setVerificationId(confirmation.verificationId);
      setStep('code');
    } catch (error) {
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      // Clear any existing reCAPTCHA
      if (window.recaptchaVerifier) {
        try {
          await window.recaptchaVerifier.clear();
          window.recaptchaVerifier = null;
        } catch (e) {
          console.log('Error clearing verifier:', e);
        }
      }

      // Remove the container
      const container = document.getElementById('recaptcha-container');
      if (container) {
        container.remove();
      }

      // Handle specific error cases
      if (error.code === 'auth/too-many-requests') {
        alert('Too many attempts. Please try again later.');
      } else if (error.code === 'auth/invalid-phone-number') {
        alert('Please enter a valid phone number with country code (e.g., +1 for US)');
      } else if (error.code === 'auth/invalid-app-credential' || error.code === 'auth/invalid-recaptcha-response') {
        alert('The verification failed. Please try again.');
      } else {
        alert('Error sending verification code. Please try again.');
      }
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
      const result = await signInWithCredential(auth, credential);
      
      // Create or update user profile
      await createUserProfile(result.user.uid, result.user.phoneNumber);
      
      // Reset form state
      setShowAuth(false);
      setStep('phone');
      setPhoneNumber('');
      setVerificationCode('');
      setVerificationId(''); // Clear verification ID after successful verification
    } catch (error) {
      console.error('Error verifying code:', error);
      if (error.code === 'auth/invalid-verification-code') {
        setError('Invalid verification code. Please try again.');
      } else if (error.code === 'auth/too-many-requests') {
        setIsRateLimited(true);
        setCooldownTimer(300); // 5 minutes
        const timer = setInterval(() => {
          setCooldownTimer((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              setIsRateLimited(false);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      setIsVerified(false);
      setCurrentUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <nav className="bg-white shadow-sm">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 md:h-16">
          <Link to="/" className="flex items-center space-x-2">
            <MoonIcon className="h-6 w-6 md:h-8 md:w-8 text-[#F5B100]" />
            <span className="text-lg md:text-xl font-bold text-black">Ramadan<span className="text-[#F5B100]">Tracker</span></span>
          </Link>
          <div className="flex items-center space-x-4">
            {!isVerified ? (
              <button
                onClick={() => setShowAuth(true)}
                className="bg-[#F5B100] text-white px-2.5 py-1.5 md:px-4 md:py-2 rounded-lg shadow-sm hover:bg-[#E5A500] transition-colors duration-200 flex items-center space-x-1 md:space-x-2 group text-xs md:text-base"
              >
                <PhoneIcon className="w-3.5 h-3.5 md:w-5 md:h-5 group-hover:scale-110 transition-transform duration-200" />
                <span className="font-medium whitespace-nowrap">Continue with Phone</span>
              </button>
            ) : (
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2 text-gray-600">
                  <div className="p-1.5 bg-[#FFF8E7] rounded-lg">
                    <PhoneIcon className="w-4 h-4 text-[#F5B100]" />
                  </div>
                  <span className="text-sm">{currentUser?.phoneNumber}</span>
                </div>
                <button
                  onClick={handleSignOut}
                  className="text-sm text-gray-600 hover:text-[#F5B100] transition-colors duration-200"
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Phone Authentication Modal */}
      {showAuth && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-8 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-black">
                {step === 'phone' ? 'Sign in with Phone' : 'Enter Verification Code'}
              </h2>
              <button
                onClick={() => {
                  setShowAuth(false);
                  setStep('phone');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            {step === 'phone' ? (
              <form onSubmit={handleSendCode} className="space-y-6">
                <p className="text-gray-600">
                  Enter your phone number to receive SMS notifications and track your fasts.
                </p>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5B100] focus:border-transparent transition-colors duration-200"
                    placeholder="+1 (555) 555-5555"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    pattern="^\+1[0-9]{10}$"
                    title="Please enter a valid US/Canada phone number starting with +1 followed by 10 digits"
                    required
                  />
                </div>
                <button 
                  type="submit" 
                  className="w-full bg-[#F5B100] text-white px-6 py-3 rounded-lg shadow-md hover:bg-[#E5A500] transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Send Code
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyCode} className="space-y-6">
                <p className="text-gray-600">
                  Enter the 6-digit code sent to your phone.
                </p>
                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    id="code"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F5B100] focus:border-transparent transition-colors duration-200"
                    placeholder="123456"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    required
                  />
                </div>
                <button type="submit" className="w-full bg-[#F5B100] text-white px-6 py-3 rounded-lg shadow-md hover:bg-[#E5A500] transition-colors duration-200 font-medium">
                  Verify Code
                </button>
                <button
                  type="button"
                  className="w-full bg-gray-100 text-gray-600 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                  onClick={() => setStep('phone')}
                >
                  Back to Phone Number
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar; 